import { TrimbimModel } from '@technology/web3d-plugin-trimbim';
import { LoadState } from '@trimble/data-loading';
import { getLogger } from '@trimble/logging';
import type { CapabilityRegistry } from '@trimble/web3d';
import {
  getTrbModelId,
  getTrbModelName,
  isTrbModelName,
  TrimBimCapabilityProvider,
} from '@trimble/web3d-providers';
import type {
  CreateObjectLinkRequest,
  SourceDataRequest,
} from '@data-access/connect/tc-tcps';
import type { ConnectIdentifierResolver } from '@technology/web3d-plugin-trimbim/dist/esnext/Extras/ConnectIdentifier/ConnectIdentifierResolver';
import {
  buildResolver,
  extractEntitySourceIds,
  resolveSourceIdToEntityIds,
} from './connect-identifier-helpers';
import {
  defaultPaneEligibility,
  type PaneEligibilityOptions,
} from './pane-eligibility';

export interface FileFolderDataState {
  readonly tcFileFolder: {
    readonly state: LoadState;
    readonly items?: Readonly<
      Record<
        string,
        { readonly type: string; readonly versionId: string } | undefined
      >
    >;
  };
}

export type IssueModelObjectHelperOptions = PaneEligibilityOptions;

interface SelectionEntry {
  trbModelName: string;
  entityIds: number[];
}

function getSelectionEntries(
  registry: CapabilityRegistry,
  options: IssueModelObjectHelperOptions = {}
): SelectionEntry[] {
  const result: SelectionEntry[] = [];
  const isPaneEligible = options.isPaneEligible ?? defaultPaneEligibility;

  for (const container of registry.paneContainers()) {
    if (!isPaneEligible(container)) continue;

    const viewer = container.viewerWrapper?.()?.viewer;
    if (!viewer) continue;

    for (const [modelId, entityIds] of viewer.selection.entries()) {
      if (!isTrbModelName(modelId)) continue;
      if (entityIds.length === 0) continue;
      result.push({ trbModelName: modelId, entityIds: [...entityIds] });
    }
  }

  return result;
}

function buildVersionToFileIdMap(
  dataState?: FileFolderDataState
): Map<string, string> {
  const map = new Map<string, string>();
  const items = dataState?.tcFileFolder.items;
  if (!items || dataState?.tcFileFolder.state !== LoadState.LoadComplete) {
    return map;
  }
  for (const [fileId, item] of Object.entries(items)) {
    if (item && item.type === 'FILE') {
      map.set(item.versionId, fileId);
    }
  }
  return map;
}

async function extractSourceData(
  model: TrimbimModel,
  entityIds: number[]
): Promise<SourceDataRequest[]> {
  const sourceIds = await extractEntitySourceIds(model, entityIds);
  return sourceIds
    .filter(
      (sourceId): sourceId is string =>
        sourceId !== undefined && sourceId !== ''
    )
    .map((sourceId) => ({ sourceId, type: 'BIMOBJECT' as const }));
}

async function resolveSourceData(
  registry: CapabilityRegistry,
  trbModelName: string,
  entityIds: number[],
  options: IssueModelObjectHelperOptions = {}
): Promise<SourceDataRequest[]> {
  const isPaneEligible = options.isPaneEligible ?? defaultPaneEligibility;

  for (const container of registry.paneContainers()) {
    if (!isPaneEligible(container)) continue;

    const viewer = container.viewerWrapper?.()?.viewer;
    if (!viewer) continue;

    const model = viewer.getModel(trbModelName) as TrimbimModel | undefined;
    if (!model) continue;

    try {
      const result = await extractSourceData(model, entityIds);
      if (result.length > 0) return result;

      const childIds = await model.getHierarchyChildren(
        entityIds,
        undefined,
        true
      );
      if (childIds.length > 0) {
        const childResult = await extractSourceData(model, childIds);
        if (childResult.length > 0) return childResult;
      }
    } catch (error) {
      getLogger().warn('Failed to resolve entity identifiers', { error });
    }
  }

  return [];
}

export function hasModelObjectSelection(
  registry: CapabilityRegistry,
  options: IssueModelObjectHelperOptions = {}
): boolean {
  return getSelectionEntries(registry, options).length > 0;
}

export async function getSelectedObjectLinkData(
  registry: CapabilityRegistry,
  issueId: string,
  contextId: string,
  dataState: FileFolderDataState,
  options: IssueModelObjectHelperOptions = {}
): Promise<CreateObjectLinkRequest | null> {
  const selectionEntries = getSelectionEntries(registry, options);
  if (selectionEntries.length === 0) return null;

  const tcFileFolder = dataState.tcFileFolder;
  if (tcFileFolder.state !== LoadState.LoadComplete || !tcFileFolder.items) {
    return null;
  }

  for (const { trbModelName, entityIds } of selectionEntries) {
    const fileId = getTrbModelId(trbModelName);
    if (!fileId) continue;

    const fileItem = tcFileFolder.items[fileId];
    if (!fileItem || fileItem.type !== 'FILE') continue;

    const sourceData = await resolveSourceData(
      registry,
      trbModelName,
      entityIds,
      options
    );
    if (sourceData.length === 0) continue;

    return {
      source: {
        versionId: fileItem.versionId,
        data: sourceData,
      },
      target: {
        type: 'ISSUE',
        id: issueId,
        contextId,
      },
    };
  }

  return null;
}

export async function selectLinkedObjects(
  registry: CapabilityRegistry,
  elements: ReadonlyArray<{
    readonly versionId: string;
    readonly sourceId: string;
  }>,
  dataState?: FileFolderDataState,
  options: IssueModelObjectHelperOptions = {}
): Promise<boolean> {
  const versionToFileId = buildVersionToFileIdMap(dataState);
  if (elements.length === 0) return false;

  const isPaneEligible = options.isPaneEligible ?? defaultPaneEligibility;
  let anySelected = false;

  for (const container of registry.paneContainers()) {
    if (!isPaneEligible(container)) continue;

    const trbProvider = container.get(TrimBimCapabilityProvider)();
    if (!trbProvider) continue;

    const viewer = trbProvider.viewer;
    viewer.selection.clear();

    const resolverCache = new Map<string, ConnectIdentifierResolver>();

    for (const element of elements) {
      const fileId = versionToFileId.get(element.versionId);
      const trbModelName = getTrbModelName(fileId ?? element.versionId);
      const model = viewer.getModel(trbModelName) as TrimbimModel | undefined;
      if (!model) continue;

      try {
        let resolver = resolverCache.get(trbModelName);
        if (!resolver) {
          resolver = await buildResolver(model);
          resolverCache.set(trbModelName, resolver);
        }

        const entityIds = resolveSourceIdToEntityIds(
          resolver,
          element.sourceId
        );
        if (entityIds.length > 0) {
          viewer.selection.add(trbModelName, entityIds);
          anySelected = true;
        }
      } catch (error) {
        getLogger().warn(
          `Failed to resolve sourceId ${element.sourceId} to entity`,
          { error }
        );
      }
    }
  }

  return anySelected;
}

export async function getSelectedSourceIds(
  registry: CapabilityRegistry,
  options: IssueModelObjectHelperOptions = {}
): Promise<string[]> {
  const selectionEntries = getSelectionEntries(registry, options);
  if (selectionEntries.length === 0) return [];

  const sourceIds: string[] = [];
  for (const { trbModelName, entityIds } of selectionEntries) {
    const sourceData = await resolveSourceData(
      registry,
      trbModelName,
      entityIds,
      options
    );
    for (const source of sourceData) {
      sourceIds.push(source.sourceId);
    }
  }
  return sourceIds;
}
